import { useState } from "react";
import { motion } from "framer-motion";
import { Terminal, Lock, ArrowRight, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState("admin@paygate.com");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [fpEmail, setFpEmail] = useState("");
  const [fpMsg, setFpMsg] = useState("");
  const [fpBusy, setFpBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setFpBusy(true);
    setFpMsg("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email: fpEmail });
      setFpMsg(data.message || "If an account with that email exists, reset instructions have been sent.");
    } catch (err) {
      setFpMsg(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setFpBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid-bg flex items-center justify-center p-6 bg-background">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <div className="mb-8 flex items-center gap-3">
          <div className="h-11 w-11 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center">
            <Terminal className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="font-heading text-xl font-bold tracking-tight">PayGate Console</h1>
            <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
              GHL · Square · Stripe
            </p>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-7 shadow-sm">
          <div className="mb-6 flex items-center gap-2 text-sm text-muted-foreground">
            {forgot ? <KeyRound className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
            {forgot ? "Reset your password" : "Admin access"}
          </div>

          {forgot ? (
            <form onSubmit={submitForgot} className="space-y-4" data-testid="forgot-form">
              <p className="text-xs text-muted-foreground">
                Enter your account email. If it exists, we'll send you a new temporary password.
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="fp-email" className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Email</Label>
                <Input
                  id="fp-email"
                  data-testid="forgot-email-input"
                  type="email"
                  value={fpEmail}
                  onChange={(e) => setFpEmail(e.target.value)}
                  placeholder="you@store.com"
                  required
                />
              </div>
              {fpMsg && (
                <p data-testid="forgot-message" className="text-sm text-emerald-400">{fpMsg}</p>
              )}
              <Button
                data-testid="forgot-submit-button"
                type="submit"
                disabled={fpBusy}
                className="w-full active:scale-95 transition-transform"
              >
                {fpBusy ? "Sending…" : "Send reset instructions"}
              </Button>
              <button
                type="button"
                data-testid="back-to-login-link"
                onClick={() => { setForgot(false); setFpMsg(""); }}
                className="w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                ← Back to sign in
              </button>
            </form>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Email</Label>
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Password</Label>
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              {error && (
                <p data-testid="login-error" className="text-sm text-destructive">{error}</p>
              )}
              <Button
                data-testid="login-submit-button"
                type="submit"
                disabled={busy}
                className="w-full active:scale-95 transition-transform"
              >
                {busy ? "Signing in…" : "Sign in"}
                <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
              <button
                type="button"
                data-testid="forgot-password-link"
                onClick={() => { setForgot(true); setError(""); setFpEmail(email); }}
                className="w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Forgot password?
              </button>
            </form>
          )}
        </div>
        <p className="mt-4 text-center text-xs text-muted-foreground font-mono">
          Secure backend for card-form checkouts
        </p>
      </motion.div>
    </div>
  );
}
